// @flow
import * as React from 'react';
import _ from 'lodash';
import $ from 'jquery';
import WavePlayerComment from './lib/WavePlayerComment';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import Authenticator from '../protocol/Authenticator';
import ArrayUtil from '../util/ArrayUtil';
import CommentUtil from '../util/CommentUtil';
import { isWaveplayerComment } from '../util/VideoUtil';
import Store from '../store/Store';
import StoreListener from '../store/StoreListener';
import { WAVEPLAYER_COMMENT, ACTIVE_COMMENT_ID, SHOW_COMMENTS } from '../store/Constants';
import type { Permissions } from '../types';
import {
  AnnotationThread,
  Annotation,
  AnnotationAttachment,
  AnnotationLocation,
  TemporalLocation
} from '../autogenerated/ZoomBridge';

import './lib/waveplayer';
import './lib/wave3Dobjectviewer';
import './lib/waveplayer.css';
import './WavePlayer.css';

const debug = require('../debug/debug')('wc:video:WavePlayer');

type Props = {
  src: string,
  comments: AnnotationThread[],
  autostart: boolean,
  framerate: any,
  startTime: number,
  vrMode: 'none' | 'monoscopic' | 'overunder' | 'sidebyside',
  renderWaveplayerComment: Function,
  permissions: Permissions,
  is3DAsset: boolean,
  WAVEPLAYER_COMMENT?: ?AnnotationThread,
  ACTIVE_COMMENT_ID?: ?number
};

type State = {};

const storeItems = [WAVEPLAYER_COMMENT, ACTIVE_COMMENT_ID];

@StoreListener(storeItems)
export default class WavePlayer extends React.Component<Props, State> {
  static defaultProps = {
    comments: [],
    autostart: true,
    vrMode: 'monoscopic'
  };

  _node: ?HTMLDivElement;
  _player: $;
  _setPositionAttempts = 0;
  _setCommentsAttempts = 0;
  _setCommentsTimer: any = null;

  componentDidMount() {
    const {
      src,
      comments,
      autostart,
      framerate,
      startTime,
      vrMode,
      permissions,
      is3DAsset,
      isCompareMode
    } = this.props;

    /*	var videoSources = {
      "mp4": mp4Version, //1080p
      "webm": webmVersion, //1080p
      "mobile_mp4": mobileMp4Version, //540p
      "mp3": mp3Version,
      "waveform": waveform,
      "poster": poster
    }; */

    const { canWriteComments } = permissions;

    if (is3DAsset) {
      debug('[componentDidMount()] initializing 3d player');
      this._player = $(this._node).wave3DViewer({
        file: src,
        fileType: 'obj',
        alwaysShowControls: true,
        enableComments: canWriteComments,
        isCompareMode: isCompareMode
      });
    } else {
      debug('[componentDidMount()] initializing waveplayer');
      this._player = $(this._node).wavePlayer({
        playlist: { mp4: src },
        alwaysShowControls: true,
        showTitleSafe: false,
        enableComments: canWriteComments,
        autostart,
        framerate,
        startTime,
        vrMode
      });
    }

    this._player
      .on('shapeDrawn.waveplayer', this.onShapeDrawn)
      .on('timecodeMarkerClick.waveplayer', this.onTimecodeMarkerClick);

    this.setPosition();
    this.setComments(comments);
    Store.put(SHOW_COMMENTS, false);
    window.addEventListener('resize', this.onResize);
  }

  componentWillReceiveProps(nextProps: Props) {
    const { comments } = nextProps;
    const commentId = nextProps[ACTIVE_COMMENT_ID];
    if (!ArrayUtil.areDeepEqual(comments, this.props.comments)) {
      this.setComments(comments);
    }

    if (!nextProps[WAVEPLAYER_COMMENT] && this.props[WAVEPLAYER_COMMENT]) {
      this._player.CancelNewComment();
    }

    if (commentId && commentId !== this.props[ACTIVE_COMMENT_ID]) {
      const comment = comments.find((c) => c.getThreadId() === commentId);
      if (comment) {
        const wpComment = new WavePlayerComment(comment);
        const { time, uniqueID, lat, lon, vrView } = wpComment;
        this._player.GoToTimeAndPopComment(time, uniqueID, lat, lon, vrView);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    this._player.Destroy();
    Store.put(SHOW_COMMENTS, true);
  }

  get currentTime() {
    return this._player.GetCurrentTime();
  }

  get duration() {
    return this._player.playerSettings.duration;
  }

  onShapeDrawn = (e: Event, shape: Object, originalEvent: MouseEvent) => {
    const { offsetX, offsetY, target } = originalEvent;
    const rect = target.getBoundingClientRect();
    const containerRect = this._node.getBoundingClientRect();
    const position = {
      top: offsetY + rect.top - containerRect.top,
      left: offsetX + rect.left - containerRect.left
    };
    const data = JSON.stringify(shape);
    const thread = this.createAnnotationThread(data, position, shape.time);
    Store.put(WAVEPLAYER_COMMENT, thread);
  };

  onTimecodeMarkerClick = (e: Event, comment: AnnotationThread) => {
    const { time, uniqueID, lat, lon, vrView } = comment;

    this._player.GoToTimeAndPopComment(time, uniqueID, lat, lon, vrView);
  };

  setComments = (comments: AnnotationThread[]) => {
    clearTimeout(this._setCommentsTimer);

    if (!this._node) {
      return;
    }

    if (!this._node.querySelector('.waveplayer_container')) {
      this._setCommentsAttempts++;

      if (this._setCommentsAttempts > 10) {
        return;
      }

      this._setCommentsTimer = setTimeout(() => this.setComments(comments), 1000);
      return;
    }

    comments.filter(isWaveplayerComment).forEach((c) => {
      const wpComment = new WavePlayerComment(c);
      this._player.AddComment.apply(null, wpComment.toArray());
    });

    this._setCommentsAttempts = 0;
  };

  setPosition = () => {
    if (!this._node) {
      return;
    }

    const contentNode = this._node.querySelector('.waveplayer_container');
    if (!contentNode) {
      this._setPositionAttempts++;
      if (this._setPositionAttempts > 10) {
        return;
      }

      return setTimeout(this.setPosition, 500);
    }

    const diffX = this._node.clientWidth - contentNode.clientWidth;
    const diffY = this._node.clientHeight - contentNode.clientHeight;
    const left = diffX > 0 ? diffX / 2 : 0;
    const top = diffY > 0 ? diffY / 2 : 0;
    $(contentNode).css({ top, left });

    this._setPositionAttempts = 0;
  };

  onResize = _.debounce(() => {
    this.setPosition();
  }, 250);

  play = () => {
    this._player.Play();
  };

  pause = () => {
    this._player.Pause();
  };

  createAnnotationThread(data: any, position: Object, time: number) {
    const attachment = new AnnotationAttachment()
      .setCreator(Authenticator.AUTH_DATA.username)
      .setData(data)
      .setDataType('SVG')
      .setFileName(WavePlayerComment.FILENAME)
      .setCommentId(0);

    const annotation = new Annotation()
      .setCreator(Authenticator.AUTH_DATA.username)
      .setAttachments([attachment])
      .setText('');

    const temporalLocation = new TemporalLocation().setTimeOffset(time * 1000);

    const annotationLocation = new AnnotationLocation()
      .setTemporalLocation(temporalLocation)
      .setCommentIndex(CommentUtil.maxCommentIndex(this.props.comments) + 1);

    const thread = new AnnotationThread()
      .setAnnotationLocation(annotationLocation)
      .setAnnotationKind(AnnotationUtil.COMMENT)
      .setAnnotation(annotation);

    AnnotationUtil.setWidthAndHeightOffset(thread, position.left, position.top);

    return thread;
  }

  render() {
    return (
      <div name="WavePlayer" ref={(node) => (this._node = node)}>
        {this.props.renderWaveplayerComment()}
      </div>
    );
  }
}
